import { useEffect, useRef, useCallback } from "react";

const TAU = Math.PI * 2;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function shade(hex, amt) {
  try {
    const c = hex.replace("#", "");
    const n = parseInt(c.length === 3 ? c.split("").map((x) => x + x).join("") : c, 16);
    let r = (n >> 16) + amt, g = ((n >> 8) & 255) + amt, b = (n & 255) + amt;
    r = Math.max(0, Math.min(255, r)); g = Math.max(0, Math.min(255, g)); b = Math.max(0, Math.min(255, b));
    return `rgb(${r},${g},${b})`;
  } catch {
    return hex;
  }
}

/**
 * Canvas spinning wheel. Controlled via the `command` prop.
 * command = { winnerIndex, nonce, duration } — a new nonce triggers a spin.
 */
export default function SpinWheel({
  items = [], size = 460, command = null, spinning, onRest, onTick, onStart,
}) {
  const canvasRef = useRef(null);
  const rotationRef = useRef(0);
  const rafRef = useRef(null);
  const lastNonce = useRef(null);
  const lastTickSlice = useRef(-1);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext("2d");
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    const cx = size / 2, cy = size / 2, radius = size / 2 - 10;
    const n = items.length;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotationRef.current);

    if (n === 0) {
      ctx.restore();
      ctx.fillStyle = "#94a3b8";
      ctx.font = "600 18px Chivo, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Nessun elemento attivo", cx, cy);
      return;
    }

    const a = TAU / n;
    for (let i = 0; i < n; i++) {
      const start = i * a, end = start + a;
      const color = items[i].color || "#1e293b";
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, start, end);
      ctx.closePath();
      const grad = ctx.createRadialGradient(0, 0, radius * 0.2, 0, 0, radius);
      grad.addColorStop(0, shade(color, 25));
      grad.addColorStop(1, color);
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = "rgba(8,10,15,0.65)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // label
      ctx.save();
      ctx.rotate(start + a / 2);
      ctx.textAlign = "right";
      ctx.fillStyle = "#ffffff";
      ctx.font = `700 ${Math.max(11, Math.min(18, 320 / n + 6))}px 'Barlow Condensed', sans-serif`;
      ctx.shadowColor = "rgba(0,0,0,0.85)";
      ctx.shadowBlur = 4;
      const label = items[i].name.length > 16 ? items[i].name.slice(0, 15) + "…" : items[i].name;
      ctx.fillText(label.toUpperCase(), radius - 14, 5);
      ctx.restore();
    }
    ctx.restore();

    // center cap
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.16, 0, TAU);
    ctx.fillStyle = "#11141d";
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#f59e0b";
    ctx.stroke();

    // pointer (top)
    ctx.beginPath();
    ctx.moveTo(cx - 16, 4);
    ctx.lineTo(cx + 16, 4);
    ctx.lineTo(cx, 34);
    ctx.closePath();
    ctx.fillStyle = "#f59e0b";
    ctx.shadowColor = "rgba(245,158,11,0.7)";
    ctx.shadowBlur = 12;
    ctx.fill();
  }, [items, size]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    if (!command || command.nonce == null || command.nonce === lastNonce.current) return;
    lastNonce.current = command.nonce;
    const n = items.length;
    if (n === 0) return;
    onStart && onStart();

    const a = TAU / n;
    const pointer = -Math.PI / 2;
    const center = (command.winnerIndex + 0.5) * a;
    const currentMod = ((rotationRef.current % TAU) + TAU) % TAU;
    const targetMod = ((pointer - center) % TAU + TAU) % TAU;
    let delta = (targetMod - currentMod + TAU) % TAU;
    const spins = 6;
    const startRot = rotationRef.current;
    const finalRot = startRot + spins * TAU + delta;
    const duration = (command.duration || 5) * 1000;
    const t0 = performance.now();
    lastTickSlice.current = -1;

    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      rotationRef.current = startRot + (finalRot - startRot) * easeOutCubic(t);
      // tick when crossing slice boundaries near pointer
      const curSlice = Math.floor(((-(rotationRef.current) - pointer) / a) % n);
      if (curSlice !== lastTickSlice.current) {
        lastTickSlice.current = curSlice;
        onTick && onTick();
      }
      draw();
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        onRest && onRest(command.winnerIndex);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="spin-wheel-canvas"
      style={{ width: size, height: size, maxWidth: "100%", filter: spinning ? "saturate(1.15)" : "none" }}
    />
  );
}
